// ── Harvi's stored verdict on a mail thread, as a badge ──────────────────────
// The Mail list carries `harvi` on a message whose thread the daily brief has
// judged (api/src/mailVerdicts.js, dbo.MailThreadVerdicts). Both clients render
// it through this one mapping, so the web and the phone badge the same thread
// the same way. Pure.

export type MailVerdictBucket = "today" | "week" | "awaiting" | "fyi" | "none";

export interface MailVerdictBadge {
  bucket: MailVerdictBucket;
  dealId?: string | null;
  dealName?: string | null;
  deadline?: string | null;
  summary?: string | null;
  judgedAt?: string | null;
  current?: boolean;
}

export const MAIL_VERDICT_LABELS: Record<Exclude<MailVerdictBucket, "none">, string> = {
  today: "Needs you today",
  awaiting: "Awaiting your reply",
  week: "This week",
  fyi: "FYI",
};

/** Tone per bucket — a key into each client's own palette, never a colour. */
export const MAIL_VERDICT_TONES: Record<Exclude<MailVerdictBucket, "none">, "urgent" | "attention" | "soon" | "quiet"> = {
  today: "urgent",
  awaiting: "attention",
  week: "soon",
  fyi: "quiet",
};

/**
 * The chip for one message, or null when there is nothing to show. A verdict
 * made before the thread moved is still shown — it is the last reading — but
 * says so, because a badge that silently describes an older state of the
 * thread is the one way this feature could mislead.
 */
export function mailVerdictChip(v: MailVerdictBadge | null | undefined):
  { label: string; tone: string; title: string; stale: boolean } | null {
  if (!v || !v.bucket || v.bucket === "none") return null;
  const label = MAIL_VERDICT_LABELS[v.bucket as Exclude<MailVerdictBucket, "none">];
  if (!label) return null;
  const stale = v.current === false;
  const parts: string[] = [];
  if (v.summary) parts.push(v.summary);
  if (v.deadline) parts.push(`Deadline: ${v.deadline}`);
  if (v.dealName) parts.push(`Deal: ${v.dealName}`);
  const when = v.judgedAt ? String(v.judgedAt).slice(0, 10) : null;
  parts.push(stale
    ? `Harvi's reading from ${when || "an earlier brief"} — new mail has arrived in this thread since.`
    : `Harvi's reading from ${when || "the daily brief"}.`);
  return {
    label: stale ? `${label} (earlier)` : label,
    tone: MAIL_VERDICT_TONES[v.bucket as Exclude<MailVerdictBucket, "none">],
    title: parts.join("\n"),
    stale,
  };
}
